import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import queryString from 'query-string';
import { updateUrl } from '@/utils';
import { Zone } from '@/__generated__/graphql';

export type ZoneDeeplinkState = {
  shouldUpdateUrl: boolean;
  selectedZoneAtom: PrimitiveAtom<Zone | null>;
  showZoneBoundariesAtom: PrimitiveAtom<boolean>;
  showNeighbouringZonesAtom: PrimitiveAtom<boolean>;
  zones: Zone[] | undefined;
};

export const useZoneDeeplink = (state: ZoneDeeplinkState) => {
  const {
    shouldUpdateUrl,
    selectedZoneAtom,
    showZoneBoundariesAtom,
    showNeighbouringZonesAtom,
    zones,
  } = state;

  const selectedZone = useAtomValue(selectedZoneAtom);
  const showZoneBoundaries = useAtomValue(showZoneBoundariesAtom);
  const showNeighbouringZones = useAtomValue(showNeighbouringZonesAtom);

  const setSelectedZone = useSetAtom(selectedZoneAtom);
  const setShowZoneBoundaries = useSetAtom(showZoneBoundariesAtom);
  const setShowNeighbouringZones = useSetAtom(showNeighbouringZonesAtom);

  useEffect(() => {
    if (shouldUpdateUrl) updateUrl('zone', selectedZone?.id);
  }, [shouldUpdateUrl, selectedZone]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('showZoneBoundaries', showZoneBoundaries ? 'true' : undefined);
    }
  }, [shouldUpdateUrl, showZoneBoundaries]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('showNeighbouringZones', showNeighbouringZones ? 'true' : undefined);
    }
  }, [shouldUpdateUrl, showNeighbouringZones]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.zone && typeof parsed.zone === 'string' && zones) {
        const matchedZone = zones.find((zone) => zone.id === parsed.zone);
        if (matchedZone) setSelectedZone(matchedZone);
      }
      if (parsed.showZoneBoundaries === 'true') setShowZoneBoundaries(true);
      if (parsed.showNeighbouringZones === 'true') setShowNeighbouringZones(true);
    },
    [zones, setSelectedZone, setShowZoneBoundaries, setShowNeighbouringZones]
  );

  return { applyFromParsed };
};
